import generateGuid from "../../../util/guid";
import * as types from '../../../constants/ComponentConstants';
import mergeDeep from "../../../util/mergeDeep";
import groupBy from "../../../util/groupBy";
import * as LeafletMapConfig from "./LeafletMapConfig";
import axios from 'axios';
import * as turf from 'turf';
import L from 'leaflet';

export default class LeafletMapHelper {
    constructor(context) {
        this.context = context;
        this.onDrawFeatureStart = this.onDrawFeatureStart.bind(this);
        this.onDrawFeatureCancel = this.onDrawFeatureCancel.bind(this);
        this.onDrawFeatureStop = this.onDrawFeatureStop.bind(this);
        this.onEditFeatureStart = this.onEditFeatureStart.bind(this);
        this._toggleFeatureEdit = this._toggleFeatureEdit.bind(this);

        this.onEditFeatureStop = this.onEditFeatureStop.bind(this);
        this.onEditFeatureCancel = this.onEditFeatureCancel.bind(this);
    }

    isPointInCircle(marker, circle) {
        const middleOfCircle = circle.getLatLng();
        const radius = circle.getRadius() / 1000;

        const getDistanceFromLatLon = (lat1, lon1, lat2, lon2) => {
            let p = 0.017453292519943295;    // Math.PI / 180
            let c = Math.cos;
            let a = 0.5 - c((lat2 - lat1) * p) / 2 +
                c(lat1 * p) * c(lat2 * p) *
                (1 - c((lon2 - lon1) * p)) / 2;

            return 12742 * Math.asin(Math.sqrt(a)); // 2 * R; R = 6371 km
        };

        const distance = getDistanceFromLatLon(marker.lat, marker.long, middleOfCircle.lat, middleOfCircle.lng);

        return distance < radius;
    }

    isPointInPolygon(marker, polygon) {
        let inside = false;
        const x = marker.lat;
        const y = marker.long;

        for (let ii = 0; ii < polygon.getLatLngs().length; ii++) {
            const polyPoints = polygon.getLatLngs()[ii];

            let minX = polyPoints[0].lat;
            let maxX = polyPoints[0].lat;
            let minY = polyPoints[0].lng;
            let maxY = polyPoints[0].lng;
            for (let n = 1; n < polyPoints.length; n++) {
                const q = polyPoints[n];
                minX = Math.min(q.lat, minX);
                maxX = Math.max(q.lat, maxX);
                minY = Math.min(q.lng, minY);
                maxY = Math.max(q.lng, maxY);
            }

            if (x < minX || x > maxX || y < minY || y > maxY) {
                return false;
            }

            for (let i = 0, j = polyPoints.length - 1; i < polyPoints.length; j = i++) {
                const xi = polyPoints[i].lat, yi = polyPoints[i].lng;
                const xj = polyPoints[j].lat, yj = polyPoints[j].lng;

                const intersect = ((yi > y) != (yj > y))
                    && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
                if (intersect) inside = !inside;
            }
        }

        return inside;
    }

    polygonIntersectsWithOtherPolygon(layer, otherLayers) {
        return Object.keys(otherLayers).some(k => {
            if (otherLayers[k].feature && otherLayers[k].feature.id.includes('Domein') && (!layer.feature || otherLayers[k].feature.id !== layer.feature.id)) { //todo replace domein by identifier
                const layerToCheckGeoJSON = layer.toGeoJSON();
                let otherLayerGeoJSON = otherLayers[k].toGeoJSON();

                try {
                    return turf.intersect(layerToCheckGeoJSON, otherLayerGeoJSON);
                }
                catch (error) {//sometimes weird errors that can be ignored due to coordinates rounding in turf.js
                    return false;
                }
            }
            return false;
        });
    }

    convertToMarkersToShow(markers) {
        let markersToShow = [];
        let groupedStuff = groupBy(markers.filter(x => x.long && x.lat && x.long != 0 && x.lat != 0).map(x => mergeDeep(x, { lngLatKey: x.long.toString() + x.lat.toString() })), 'lngLatKey');
        groupedStuff.forEach(group => {
            const moreThanOneMarker = group.length > 1;
            let marker = group[0];

            const newMarker = group.find(m => !m.feature.properties['DomeinID']); //todo replace domein by identifier

            marker = mergeDeep(newMarker ? newMarker : marker, {
                size: moreThanOneMarker ? 'xx-large' : 'x-large',
                color: newMarker && !newMarker.edited ? types.COLOR_RED : moreThanOneMarker ? types.COLOR_BLUE : types.COLOR_GREEN,
                otherInfrastructurenOnThisSpot: group.length - 1
            });

            markersToShow.push(marker);
        });

        return markersToShow;
    }

    // calculateMarkersToShowInPolygon = (layer) => {
    //     let markersInsidePolygon = [];

    //     this.props.markers.forEach(m => {
    //         if (this.helper.isPointInPolygon(m, layer))
    //             markersInsidePolygon.push(m);
    //     });

    //     this.setState({
    //         markersToShow: this.helper.convertToMarkersToShow(markersInsidePolygon)
    //     });
    // }

    // calculateMarkersToShowInCircle = (layer) => {
    //     let markersInsideCircle = [];

    //     this.props.markers.forEach(m => {
    //         if (this.helper.isPointInCircle(m, layer))
    //             markersInsideCircle.push(m);
    //     });

    //     layer.on('click', this.clickEditHandler);

    //     this.setState({
    //         markersToShow: this.helper.convertToMarkersToShow(markersInsideCircle)
    //     });
    // }

    onDrawFeatureStart() {
        this.context.setState({ isEditingRoute: true, hideMap: false });
        this.context.map.leafletElement.pm.setGlobalOptions({ snappable: false });
        this.context.map.leafletElement.pm.enableDraw('Line', LeafletMapConfig.DrawOptions);

        const customCenter = this.context.props.customCenter;

        if (customCenter) {
            this.context.map.leafletElement.panTo(new L.LatLng(customCenter[0], customCenter[1]));
            this.context.map.leafletElement.setZoom(14);
        }

        this.context.props.handleStartDrawingRoute();
    }

    onDrawFeatureCancel(e) {
        this.context.setState({ isEditingRoute: false });
        this.context.map.leafletElement.pm.disableDraw('Line');
        this.context.props.handleCancelRouteCreate();

        if (this.context.state.editingLayerLeafletId)
            this.context.map.leafletElement.eachLayer(l => {
                if (this.context.state.editingLayerLeafletId === l._leaflet_id) {
                    l.remove();
                    this.context.setState({ editingLayerLeafletId: null });
                }
            });
    }

    onDrawFeatureStop() {
        this.context.setState({ isEditingRoute: false });

        let editingLayer = null;
        this.context.map.leafletElement.eachLayer(l => {
            if (this.context.state.editingLayerLeafletId === l._leaflet_id)
                editingLayer = l;
        });

        if (editingLayer) {
            this.context.props.handleCreatefeature(editingLayer.toGeoJSON());
            this.context.setState({ editingLayerLeafletId: null });
            editingLayer.remove();
        }
        else {
            this.context.props.handleCreatefeature(null);
            this.context.setState({ editingLayerLeafletId: null });
        }
    }

    onEditFeatureStart() {
        const result = this.context.props.handleEditFeature(this._toggleFeatureEdit);
        result.then(isValid => {
            if (isValid)
                this.context.setState({ isEditingRoute: true });
            else
                this.context.setState({ isEditingRoute: false });
        });
    }

    _toggleFeatureEdit() {
        this.context.map.leafletElement.eachLayer(l => {

            if (l.feature && l.feature.properties && l.feature.properties.name && l.feature.properties.name === this.context.props.routeFeatureName) {
                this.context.map.leafletElement.pm.setGlobalOptions({ snappable: false });
                l.pm.toggleEdit();
                this.context.setState({ editingLayerLeafletId: l._leaflet_id });
            }
        });
    }

    onEditFeatureStop() {
        this.context.setState({ isEditingRoute: false });

        let editingLayer = null;
        this.context.map.leafletElement.eachLayer(l => {
            if (l.feature && l.feature.properties && l.feature.properties.name && l.feature.properties.name === this.context.props.routeFeatureName) {
                editingLayer = l;
                l.pm.toggleEdit();
            }
        });

        if (editingLayer) {
            this.context.props.handleUpdatefeature(editingLayer.toGeoJSON());
            this.context.setState({ editingLayerLeafletId: null });
        }
        else {
            this.context.props.handleUpdatefeature(null);
            this.context.setState({ editingLayerLeafletId: null });
        }
    }

    onEditFeatureCancel() {
        this.context.setState({ isEditingRoute: false });

        this.context.map.leafletElement.eachLayer(l => {
            if (l.feature && l.feature.properties && l.feature.properties.name && l.feature.properties.name === this.context.props.routeFeatureName) {
                l.remove();
            }
            this.context.props.handleCancelRouteEdit();
            this.context.setState({ editingLayerLeafletId: null });
        });
    }
}